import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  UploadCloud,
  Sparkles,
  FileSearch,
  MessagesSquare,
  ShieldCheck,
  Zap,
  UserX,
  Scale,
  FileText,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UploadModal from "../components/UploadModal";
import SEO from "../components/SEO";

const stats = [
  { value: "24.5 Cr", label: "Motor policies in India" },
  { value: "< 8%", label: "Have read their policy" },
  { value: "₹6,200 Cr", label: "Claims rejected annually" },
  { value: "Free", label: "Always, for users" },
];

const steps = [
  {
    icon: UploadCloud,
    title: "Upload your policy",
    desc: "PDF or scanned image. Any insurer. Digital or physical document.",
  },
  {
    icon: Sparkles,
    title: "AI reads every clause",
    desc: "Our AI extracts and analyses every clause — coverages, exclusions, conditions, and limits.",
  },
  {
    icon: FileSearch,
    title: "Get your clarity report",
    desc: "Plain-English summary. Red flags ranked HIGH / MED. Clarity score. No jargon.",
  },
  {
    icon: MessagesSquare,
    title: "Ask AI anything",
    desc: "\"Will flood damage be covered?\" Get instant answers grounded in your actual policy text.",
  },
];

const trustBadges = [
  { icon: Zap, title: "2 Min", subtitle: "Analysis time" },
  { icon: ShieldCheck, title: "Free", subtitle: "Always, for users" },
  { icon: UserX, title: "No agents", subtitle: "No commission. No bias." },
  { icon: Scale, title: "IRDAI-safe", subtitle: "We explain. Never advise." },
];

export default function Home() {
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO path="/" />
      <Navbar onUploadClick={() => setUploadOpen(true)} />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12 text-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium tracking-wide"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            Motor Insurance · AI-Powered Clarity
          </span>

          <h1
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900"
            data-testid="hero-title"
          >
            Understand your policy.
            <br />
            <span className="italic font-normal text-slate-500">
              Not just pay for it.
            </span>
          </h1>

          <p
            className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Upload your motor insurance PDF. In under 2 minutes, get a
            plain-English breakdown of every clause — what&apos;s covered, what&apos;s
            excluded, and what could deny your claim.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="hero-upload-btn"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-3.5 font-medium shadow-sm hover:shadow-md"
            >
              Upload My Policy
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/dashboard"
              data-testid="hero-dashboard-btn"
              className="inline-flex items-center gap-2 border border-slate-200 bg-white hover:border-slate-300 text-slate-900 rounded-full px-6 py-3.5 font-medium"
            >
              Go to Dashboard
            </Link>
          </div>

          {/* Stats */}
          <div
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl mx-auto"
            data-testid="hero-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="text-left md:text-center">
                <div className="font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
                  {s.value}
                </div>
                <div className="mt-1 text-xs sm:text-sm text-slate-500">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upload preview card */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div
          onClick={() => setUploadOpen(true)}
          data-testid="landing-upload-card"
          className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/30 rounded-3xl p-8 md:p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
            <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-white border border-slate-200 text-blue-600 shadow-sm">
              <FileText className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Upload Your Policy
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                PDF or photo · Any insurer · Take a photo, choose from your gallery, or pick a file
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
                {[
                  "What's covered — in plain English",
                  "Red flags & exclusions that could deny your claim",
                  "Add-on coverage gaps you may not know about",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setUploadOpen(true);
              }}
              data-testid="landing-upload-card-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-3 text-sm font-medium shadow-sm"
            >
              Upload now
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28"
        data-testid="how-it-works"
      >
        <div className="text-center mb-14">
          <span className="text-xs font-medium tracking-widest uppercase text-blue-600">
            How It Works
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">
            From PDF to clarity in 4 steps
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-transform"
              data-testid={`step-card-${i + 1}`}
            >
              <div className="absolute top-4 right-5 step-number select-none">
                0{i + 1}
              </div>
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustBadges.map((b) => (
              <div
                key={b.title}
                className="flex items-start gap-3"
                data-testid={`trust-${b.title.replace(/\s/g, "-").toLowerCase()}`}
              >
                <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm">
                  <b.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display font-semibold text-slate-900">
                    {b.title}
                  </div>
                  <div className="text-sm text-slate-500">{b.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-blue-600/30 blur-3xl" />
          <div className="absolute -left-10 top-0 h-40 w-40 rounded-full bg-blue-500/20 blur-2xl" />
          <div className="relative max-w-2xl">
            <h3 className="font-display text-3xl md:text-4xl tracking-tight">
              Ready to actually understand your policy?
            </h3>
            <p className="mt-3 text-slate-300">
              Free, private, and takes 2 minutes. No signup required to see your
              first report.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setUploadOpen(true)}
                data-testid="cta-upload-btn"
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 py-3.5 font-medium"
              >
                Upload My Policy
                <ArrowRight className="h-4 w-4" />
              </button>
              <Link
                to="/ask"
                data-testid="cta-ask-btn"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 rounded-full px-6 py-3.5 font-medium text-white"
              >
                Try Ask AI
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}
